/* Web Push subscription helpers.
 *   - `pushSupported()` — cheap capability check
 *   - `getPushState()` — 'unsupported' | 'denied' | 'default' | 'subscribed' | 'unsubscribed'
 *   - `subscribePush()` / `unsubscribePush()` — flip the switch
 *   - `testPush()` — trigger the backend test push
 */
import { apiClient } from './api';

const b64uToUint8 = (b64) => {
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const pushSupported = () => typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

export const getPushState = async () => {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) return 'subscribed';
  } catch { /* fall through */ }
  return Notification.permission === 'granted' ? 'unsubscribed' : 'default';
};

export const subscribePush = async () => {
  if (!pushSupported()) throw new Error('Push notifications are not supported in this browser');
  const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Permission denied');
  const { data: keyRes } = await apiClient.get('/push/vapid-public-key');
  const publicKey = b64uToUint8(keyRes.public_key);
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) await existing.unsubscribe();
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: publicKey });
  const raw = sub.toJSON();
  await apiClient.post('/push/subscribe', {
    endpoint: raw.endpoint,
    keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
    user_agent: navigator.userAgent,
  });
  return true;
};

export const unsubscribePush = async () => {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try { await apiClient.post('/push/unsubscribe', { endpoint }); } catch { /* ok */ }
  return true;
};

export const testPush = async () => (await apiClient.post('/push/test')).data;
export const getPushPreferences = async () => (await apiClient.get('/push/preferences')).data;
export const putPushPreferences = async (p) => (await apiClient.put('/push/preferences', p)).data;
